import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import Menu from '@mui/material/Menu';
import { getAuditLogs, formatAuditLog } from '../../../services/auditLogger';
import { Hub } from 'aws-amplify';
import dayjs from 'dayjs';
import { HEADER_HEIGHT } from '../utils/values';
import { AdminActionType, AuditLog, EntityType } from '../utils/types';
import {
  formatActionName,
  formatTimestamp,
  getActionColor,
  getEntityBadgeColor,
  generateChangesSummary
} from '../utils/reportingUtils';
import { dateFormat, timeFormat } from '../../../common';

interface ReportingLogsProps {
  onClose?: () => void;
}

const PAGE_SIZE = 25;

export default function ReportingLogs({ onClose }: ReportingLogsProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);

  // Add Hub listener to monitor DataStore sync events for AdminActionHistory
  useEffect(() => {
    const listener = Hub.listen('datastore', ({ payload: { event, data } }) => {
      if (event === 'modelSynced' && data.model.name === 'AdminActionHistory') {
        console.log('AdminActionHistory sync event:', data);
        console.log('Sync counts:', data.counts);
      }
    });

    return () => {
      listener();
    };
  }, []);

  const loadAuditLogs = useCallback(async () => {
    try {
      setIsLoading(true);

      // Calculate date range
      let filters: any = {};

      if (dateRange !== 'all') {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - parseInt(dateRange));

        filters = {
          startDate,
          endDate
        };
      }

      const logs = await getAuditLogs(filters);

      // Format logs for display
      const formattedLogs: AuditLog[] = logs.map((log) => {
        const formatted = formatAuditLog(log);

        // Extract entity type and ID from the action or data
        let entityType = EntityType.UNKNOWN;
        let entityId = 'N/A';
        const actionType = formatted.action;

        if (actionType === AdminActionType.MANIFEST_COMPLETE || actionType === AdminActionType.MANIFEST_DELETE) {
          entityType = EntityType.Manifest;
          entityId =
            formatted.after?.manifestId ||
            formatted.before?.manifestId ||
            formatted.after?.id ||
            formatted.before?.id ||
            'N/A';
        } else if (
          actionType === AdminActionType.SSCC_ADD_TO_MANIFEST ||
          actionType === AdminActionType.SSCC_COMPLETE ||
          actionType === AdminActionType.SSCC_DELETE ||
          actionType === AdminActionType.SSCC_REMOVE_FROM_MANIFEST
        ) {
          entityType = EntityType.SSCC;
          entityId =
            formatted.after?.ssccId || formatted.before?.ssccId || formatted.after?.id || formatted.before?.id || 'N/A';
        } else if (
          actionType === AdminActionType.ASN_ASSIGN_TO_SSCC ||
          actionType === AdminActionType.ASN_REMOVE_FROM_SSCC
        ) {
          entityType = EntityType.ASN;
          // Handle different ASN action formats
          if (formatted.action === 'ASN Remove from SSCC') {
            // For bulk ASN delete, show the list of removed ASNs or count
            const removedList =
              formatted.after?.asnsRemovedList || formatted.before?.asnsToBeRemoved?.map((a: any) => a.asn) || [];
            entityId =
              removedList.length > 0
                ? removedList.length <= 2
                  ? removedList.join(', ')
                  : `${removedList.slice(0, 2).join(', ')} +${removedList.length - 2} more`
                : formatted.before?.ssccId || 'N/A';
          } else {
            entityId =
              formatted.after?.asnId ||
              formatted.before?.asnId ||
              formatted.after?.asn ||
              formatted.before?.asn ||
              'N/A';
          }
        } else if (
          actionType === AdminActionType.QM_ROLE_SET_NO_ACCESS ||
          actionType === AdminActionType.QM_ROLE_SET_READ_ONLY ||
          actionType === AdminActionType.QM_ROLE_SET_EDITABLE
        ) {
          entityType = EntityType.Access;
          entityId = formatted.before?.['email'] || formatted.after?.['email'] || 'N/A';
        } else if (actionType === AdminActionType.LOCATION_CREATE || actionType === AdminActionType.LOCATION_UPDATE) {
          entityType = EntityType.Location;
          entityId =
            formatted.after?.name || formatted.before?.name || formatted.after?.id || formatted.before?.id || 'N/A';
        } else if (actionType === AdminActionType.PRINTER_CREATE || actionType === AdminActionType.PRINTER_UPDATE) {
          entityType = EntityType.Printer;
          entityId =
            formatted.after?.name || formatted.before?.name || formatted.after?.id || formatted.before?.id || 'N/A';
        } else if (
          actionType === AdminActionType.ADMIN_PANEL_GRANT_ACCESS ||
          actionType === AdminActionType.ADMIN_PANEL_REVOKE_ACCESS
        ) {
          entityType = EntityType.Access;
          entityId = formatted.before?.['email'] || formatted.after?.['email'] || 'N/A';
        }

        const safeEntityId = String(entityId || 'N/A');

        // Compute full (non-truncated) entity ID for ASN Remove from SSCC
        let fullEntityId = safeEntityId;
        if (formatted.action === 'ASN Remove from SSCC') {
          const removedList =
            formatted.after?.asnsRemovedList || formatted.before?.asnsToBeRemoved?.map((a: any) => a.asn) || [];
          fullEntityId = removedList.length > 0 ? removedList.join(', ') : formatted.before?.ssccId || 'N/A';
        }

        // Compute full (non-truncated) changes summary
        const fullChanges = generateChangesSummary(formatted.before, formatted.after, false) || 'No changes recorded';

        return {
          id: formatted.id,
          timestamp: formatted.timestamp ? dayjs(formatted.timestamp).format('YYYY-MM-DD HH:mm:ss') : 'N/A',
          user: formatted.user || 'Unknown',
          action: formatted.action || 'Unknown Action',
          entityType,
          entityId: safeEntityId,
          fullEntityId,
          changes: formatted.changes || 'No changes recorded',
          fullChanges,
          reason: formatted.reason
        };
      });

      setAuditLogs(formattedLogs);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      setAuditLogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange]);

  // Fetch audit logs from DynamoDB
  useEffect(() => {
    loadAuditLogs();
  }, [dateRange, loadAuditLogs]);

  // Filter audit logs based on search and entity filter
  const filteredAuditLogs = auditLogs.filter((log) => {
    const normalizedQuery = searchQuery.toLowerCase();

    // Search filter
    const matchesSearch =
      !searchQuery ||
      log.id.toLowerCase().includes(normalizedQuery) ||
      log.user.toLowerCase().includes(normalizedQuery) ||
      log.entityId.toLowerCase().includes(normalizedQuery) ||
      log.action.toLowerCase().includes(normalizedQuery) ||
      (log.changes || '').toLowerCase().includes(normalizedQuery) ||
      (log.reason || '').toLowerCase().includes(normalizedQuery);

    // Entity filter
    const matchesEntity = entityFilter === 'all' || log.entityType === entityFilter;

    return matchesSearch && matchesEntity;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredAuditLogs.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, filteredAuditLogs.length);
  const paginatedAuditLogs = filteredAuditLogs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, entityFilter, dateRange]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget as HTMLElement);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const exportToCSV = () => {
    const headers = ['Timestamp', 'User', 'Action', 'Entity Type', 'Entity ID', 'Changes', 'Reason'];
    const csvContent = [
      headers.join(','),
      ...filteredAuditLogs.map((log) =>
        [
          log.timestamp,
          log.user,
          log.action,
          log.entityType,
          log.entityId,
          `"${log.changes.replace(/"/g, '""')}"`,
          `"${(log.reason || 'No reason provided').replace(/"/g, '""')}"`
        ].join(',')
      )
    ].join('\n');

    downloadFile(csvContent, `audit-logs-${dayjs().format('YYYY-MM-DD-HHmmss')}.csv`, 'text/csv');
    handleExportMenuClose();
  };

  const exportToJSON = () => {
    const jsonContent = JSON.stringify(filteredAuditLogs, null, 2);
    downloadFile(jsonContent, `audit-logs-${dayjs().format('YYYY-MM-DD-HHmmss')}.json`, 'application/json');
    handleExportMenuClose();
  };

  const downloadFile = (content: string, filename: string, contentType: string) => {
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Stack
      spacing={0}
      sx={{
        mt: `${HEADER_HEIGHT}px`,
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
      <Box
        sx={{
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 8,
          px: 3,
          pt: 3,
          pb: 1.5,
          backgroundColor: 'background.default'
        }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', letterSpacing: '-0.3px' }}>
              Reporting Logs
            </Typography>

            {onClose && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
                onClick={onClose}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem'
                }}>
                Close
              </Button>
            )}
          </Stack>

          {/* Filters */}
          <Card
            sx={{
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: '1px solid rgba(0,0,0,0.06)',
              borderRadius: 2
            }}>
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                    <SearchIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} sx={{ color: 'text.primary' }}>
                    Search & Filter
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    fullWidth
                    placeholder="Search by ID, user, or entity..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(0,0,0,0.02)',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.03)'
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'background.default'
                        }
                      }
                    }}
                  />
                  <FormControl sx={{ minWidth: 180 }} size="small">
                    <InputLabel>Entity Type</InputLabel>
                    <Select
                      value={entityFilter}
                      onChange={(e) => setEntityFilter(e.target.value)}
                      label="Entity Type"
                      sx={{
                        backgroundColor: 'rgba(0,0,0,0.02)',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.03)'
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'background.default'
                        }
                      }}>
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="Manifest">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: '#1976d2'
                            }}
                          />
                          <span>Manifest</span>
                        </Stack>
                      </MenuItem>
                      <MenuItem value="SSCC">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: '#9c27b0'
                            }}
                          />
                          <span>SSCC</span>
                        </Stack>
                      </MenuItem>
                      <MenuItem value="ASN">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: '#00796b'
                            }}
                          />
                          <span>ASN</span>
                        </Stack>
                      </MenuItem>
                      <MenuItem value="Access">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#790000' }} />
                          <span>Access</span>
                        </Stack>
                      </MenuItem>
                      <MenuItem value="Location">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#5d4037' }} />
                          <span>Location</span>
                        </Stack>
                      </MenuItem>
                      <MenuItem value="Printer">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#455a64' }} />
                          <span>Printer</span>
                        </Stack>
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl sx={{ minWidth: 180 }} size="small">
                    <InputLabel>Date Range</InputLabel>
                    <Select
                      value={dateRange}
                      onChange={(e) => setDateRange(e.target.value)}
                      label="Date Range"
                      sx={{
                        backgroundColor: 'rgba(0,0,0,0.02)',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.03)'
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'background.default'
                        }
                      }}>
                      <MenuItem value="1">Last 24 hours</MenuItem>
                      <MenuItem value="7">Last 7 days</MenuItem>
                      <MenuItem value="30">Last 30 days</MenuItem>
                      <MenuItem value="90">Last 90 days</MenuItem>
                      <MenuItem value="all">All Time</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'hidden',
          overflowX: 'hidden',
          px: 3,
          pb: 2
        }}>
        {/* Audit Logs Card */}
        <Card
          sx={{
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
          <Box
            sx={{
              px: 2,
              py: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}>
            <Stack spacing={0} sx={{ height: '100%' }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  flexShrink: 0,
                  backgroundColor: 'background.paper',
                  py: 0.5,
                  borderBottom: '1px solid rgba(0,0,0,0.08)'
                }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6" fontWeight={600}>
                    Audit Logs ({filteredAuditLogs.length})
                  </Typography>
                </Stack>
                <Box>
                  <Tooltip title="Export audit logs">
                    <Box
                      onClick={filteredAuditLogs.length > 0 ? handleExportMenuOpen : undefined}
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        backgroundColor: filteredAuditLogs.length > 0 ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0,0,0,0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: filteredAuditLogs.length > 0 ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor:
                            filteredAuditLogs.length > 0 ? 'rgba(25, 118, 210, 0.15)' : 'rgba(0,0,0,0.04)'
                        }
                      }}>
                      <DownloadIcon
                        sx={{
                          color: filteredAuditLogs.length > 0 ? '#1976d2' : 'rgba(0,0,0,0.26)',
                          fontSize: 20
                        }}
                      />
                    </Box>
                  </Tooltip>
                  <Menu
                    anchorEl={exportMenuAnchor}
                    open={Boolean(exportMenuAnchor)}
                    onClose={handleExportMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right'
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: 1.5
                      }
                    }}>
                    <MenuItem
                      onClick={exportToCSV}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(46, 125, 50, 0.08)'
                        }
                      }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            p: 0.75,
                            borderRadius: 1,
                            backgroundColor: 'rgba(46, 125, 50, 0.1)',
                            display: 'flex'
                          }}>
                          <AssessmentIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            Export as CSV
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {filteredAuditLogs.length} records • Spreadsheet format
                          </Typography>
                        </Box>
                      </Stack>
                    </MenuItem>
                    <MenuItem
                      onClick={exportToJSON}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)'
                        }
                      }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            p: 0.75,
                            borderRadius: 1,
                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                            display: 'flex'
                          }}>
                          <AssessmentIcon fontSize="small" sx={{ color: '#1976d2' }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            Export as JSON
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {filteredAuditLogs.length} records • Raw data format
                          </Typography>
                        </Box>
                      </Stack>
                    </MenuItem>
                  </Menu>
                </Box>
              </Stack>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pt: 2
                }}>
                {isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress />
                  </Box>
                ) : filteredAuditLogs.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <HistoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No audit logs found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchQuery || entityFilter !== 'all'
                        ? 'Try adjusting your filters'
                        : 'Audit logs will appear here when admin actions are performed'}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ overflow: 'auto', maxHeight: '100%' }}>
                    <Table stickyHeader>
                      <TableHead
                        sx={{
                          '& .MuiTableCell-head': {
                            backgroundColor: 'background.paper',
                            zIndex: 1
                          }
                        }}>
                        <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: 0.5,
                              py: 1.5
                            }}>
                            Timestamp
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: 0.5,
                              py: 1.5
                            }}>
                            User
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: 0.5,
                              py: 1.5
                            }}>
                            Action
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: 0.5,
                              py: 1.5
                            }}>
                            Entity
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: 0.5,
                              py: 1.5
                            }}>
                            Changes
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: 0.5,
                              py: 1.5
                            }}>
                            Reason
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: 600,
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: 0.5,
                              py: 1.5
                            }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedAuditLogs.map((log) => (
                          <TableRow
                            key={log.id}
                            hover
                            sx={{
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                              borderLeft: `3px solid ${getEntityBadgeColor(log.entityType)}`
                            }}>
                            <TableCell sx={{ whiteSpace: 'nowrap', py: 2 }}>
                              <Typography variant="body2" fontWeight={500}>
                                {formatTimestamp(log.timestamp)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', fontSize: '0.65rem' }}>
                                {`${dayjs(log.timestamp).format(dateFormat)} ${dayjs(log.timestamp).format(timeFormat)}`}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2" fontWeight={500}>
                                {log.user}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 200,
                                  px: 2,
                                  py: 0.75,
                                  borderRadius: 1,
                                  backgroundColor: getActionColor(log.action).bg,
                                  border: `1px solid ${getActionColor(log.action).border}`,
                                  color: getActionColor(log.action).text
                                }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  sx={{
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: getActionColor(log.action).text
                                  }}>
                                  {formatActionName(log.action)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Stack spacing={0.5}>
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      backgroundColor: getEntityBadgeColor(log.entityType)
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      textTransform: 'uppercase',
                                      letterSpacing: 0.5,
                                      fontSize: '0.7rem'
                                    }}>
                                    {log.entityType}
                                  </Typography>
                                </Stack>
                                <Typography variant="body2" fontWeight={600} sx={{ pl: 1.5 }}>
                                  {log.entityId}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2" sx={{ maxWidth: 300, color: 'text.secondary' }}>
                                {log.changes}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  maxWidth: 250,
                                  fontStyle: log.reason ? 'normal' : 'italic'
                                }}>
                                {log.reason || 'No reason provided'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  sx={{ color: 'text.secondary' }}
                                  onClick={() => {
                                    setSelectedLog(log);
                                    setOpenDetailsDialog(true);
                                  }}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            </Stack>
          </Box>
        </Card>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          position: 'sticky',
          bottom: 0,
          zIndex: 8,
          borderTop: '1px solid rgba(0,0,0,0.08)',
          backgroundColor: 'background.default',
          px: 3,
          py: 1.5,
          minHeight: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        {!isLoading && totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        )}
      </Box>

      {/* Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: 'background.paper'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'background.default',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <HistoryIcon sx={{ fontSize: 22, color: '#1976d2' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Audit Log Details
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Complete record of admin action
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 0, py: 0 }}>
          {selectedLog && (
            <Box>
              {/* Overview Section */}
              <Box sx={{ px: 3, py: 3, backgroundColor: 'background.default' }}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    mb: 2,
                    display: 'block'
                  }}>
                  Overview
                </Typography>
                <Stack direction="row" spacing={3}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Log ID
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                      {selectedLog.id}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Timestamp
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                      {selectedLog.timestamp}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(selectedLog.timestamp)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Performed By
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                      {selectedLog.user}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              {/* Action Section */}
              <Box sx={{ px: 3, py: 3, backgroundColor: 'background.paper' }}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    mb: 2,
                    display: 'block'
                  }}>
                  Action Details
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.75rem', mb: 1, display: 'block' }}>
                      Action Type
                    </Typography>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        px: 2.5,
                        py: 1,
                        borderRadius: 1.5,
                        backgroundColor: getActionColor(selectedLog.action).bg,
                        border: `1.5px solid ${getActionColor(selectedLog.action).border}`,
                        mt: 1
                      }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: getActionColor(selectedLog.action).text }}>
                        {selectedLog.action}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                      Affected Entity
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.5,
                        px: 2,
                        py: 1.5,
                        borderRadius: 1.5,
                        backgroundColor: 'background.default',
                        border: '1px solid rgba(0,0,0,0.08)'
                      }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: getEntityBadgeColor(selectedLog.entityType)
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'text.secondary'
                          }}>
                          {selectedLog.entityType}
                        </Typography>
                        <Divider orientation="vertical" flexItem />
                        <Typography variant="body2" fontWeight={600} sx={{}}>
                          {selectedLog.fullEntityId}
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              {/* Changes Section */}
              <Box sx={{ px: 3, py: 3, backgroundColor: 'background.default' }}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    mb: 2,
                    display: 'block'
                  }}>
                  Changes Made
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    backgroundColor: 'background.paper',
                    border: '1px solid rgba(0,0,0,0.08)'
                  }}>
                  <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                    {selectedLog.fullChanges}
                  </Typography>
                </Box>
              </Box>

              {/* Reason Section */}
              {selectedLog.reason && (
                <>
                  <Divider />
                  <Box sx={{ px: 3, py: 3, backgroundColor: 'background.paper' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        mb: 2,
                        display: 'block'
                      }}>
                      Reason Provided
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1.5,
                        backgroundColor: 'background.default',
                        border: '1px solid rgba(0,0,0,0.08)'
                      }}>
                      <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                        {selectedLog.reason}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: 'background.paper',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}>
          <Button
            onClick={() => setOpenDetailsDialog(false)}
            variant="contained"
            sx={{
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none'
              }
            }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
